/**
 * Live-Verbindung (Server-Sent Events): hält Bibliothek, Setlists, Einstellungen
 * und die Live-Session auf allen Geräten synchron.
 */
import type { PresenceDto, ServerEvent, UserSettings } from "@piano/shared";
import { DEFAULT_USER_SETTINGS } from "@piano/shared";
import { apiUrl, clientId } from "../api/client.js";
import { api } from "../api/index.js";
import { hue } from "./hue.svelte.js";
import { library } from "./library.svelte.js";
import { playlists } from "./playlists.svelte.js";
import { session } from "./session.svelte.js";

type PrefsListener = (documentId: string) => void;

class Live {
  connected = $state(false);
  presence = $state<PresenceDto[]>([]);
  settings = $state<UserSettings>(DEFAULT_USER_SETTINGS);
  private source: EventSource | null = null;
  private prefsListeners = new Set<PrefsListener>();

  /** Andere Geräte, die gerade ein Stück offen haben (z. B. das iPad am Flügel). */
  others = $derived(this.presence.filter((p) => p.clientId !== clientId && p.documentId));

  start(): void {
    this.stop();
    const source = new EventSource(apiUrl(`api/events?clientId=${encodeURIComponent(clientId)}`), {
      withCredentials: true,
    });
    this.source = source;
    source.onopen = () => {
      this.connected = true;
      // Nach (Wieder-)Verbindung alles frisch laden – verpasste Ereignisse gibt es nicht.
      void this.refreshAll();
    };
    source.onerror = () => {
      this.connected = false;
    };
    const handle = (raw: MessageEvent<string>) => {
      let event: ServerEvent;
      try {
        event = JSON.parse(raw.data) as ServerEvent;
      } catch {
        return;
      }
      this.dispatch(event);
    };
    for (const type of [
      "library",
      "categories",
      "playlists",
      "settings",
      "presence",
      "document-prefs",
      "hue",
      "session-revoked",
    ]) {
      source.addEventListener(type, handle as EventListener);
    }
    document.addEventListener("visibilitychange", this.onVisible);
  }

  stop(): void {
    this.source?.close();
    this.source = null;
    this.connected = false;
    document.removeEventListener("visibilitychange", this.onVisible);
  }

  onDocumentPrefs(listener: PrefsListener): () => void {
    this.prefsListeners.add(listener);
    return () => this.prefsListeners.delete(listener);
  }

  async refreshAll(): Promise<void> {
    await Promise.allSettled([
      library.load(),
      playlists.load(),
      this.loadSettings(),
      this.loadPresence(),
      hue.refresh(),
    ]);
  }

  async loadSettings(): Promise<void> {
    this.settings = await api.settings();
  }

  async loadPresence(): Promise<void> {
    this.presence = await api.presence();
  }

  private onVisible = () => {
    if (document.visibilityState === "visible" && this.source?.readyState === EventSource.CLOSED)
      this.start();
  };

  private dispatch(event: ServerEvent): void {
    if (event.type === "session-revoked") {
      session.expire();
      return;
    }
    // Eigene Änderungen sind schon angewendet (optimistisch bzw. aus der Antwort).
    if ("originClientId" in event && event.originClientId === clientId && event.type !== "presence") return;
    switch (event.type) {
      case "library":
        void library.load();
        break;
      case "categories":
        void library.loadCategories();
        break;
      case "playlists":
        void playlists.load();
        break;
      case "settings":
        void this.loadSettings();
        break;
      case "presence":
        void this.loadPresence();
        break;
      case "document-prefs":
        this.prefsListeners.forEach((listener) => listener(event.documentId));
        break;
      case "hue":
        void hue.refresh();
        break;
    }
  }
}

export const live = new Live();
